import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './CursorTooltip.css';

const OFFSET_X = 14;
const OFFSET_Y = 16;
const MAX_WIDTH = 280;

function clampPosition(x, y) {
  if (typeof window === 'undefined') return { x, y };
  const pad = 8;
  const w = Math.min(MAX_WIDTH, window.innerWidth - pad * 2);
  const left = Math.min(Math.max(pad, x + OFFSET_X), window.innerWidth - w - pad);
  const top = Math.min(Math.max(pad, y + OFFSET_Y), window.innerHeight - 48);
  return { left, top, maxWidth: w };
}

/**
 * Fare imlecini takip eden küçük bilgi balonu (varsayılan HTML title yerine).
 */
export function CursorTooltip({ content, children, disabled = false, className = '' }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, maxWidth: MAX_WIDTH });

  const hasContent = content != null && String(content).trim() !== '';

  const updatePos = useCallback((clientX, clientY) => {
    setPos(clampPosition(clientX, clientY));
  }, []);

  const handleEnter = useCallback(
    (e) => {
      if (disabled || !hasContent) return;
      let x = e.clientX;
      let y = e.clientY;
      if ((x == null || y == null) && e.currentTarget?.getBoundingClientRect) {
        const r = e.currentTarget.getBoundingClientRect();
        x = r.left + r.width / 2;
        y = r.top + r.height / 2;
      }
      updatePos(x ?? 0, y ?? 0);
      setVisible(true);
    },
    [disabled, hasContent, updatePos],
  );

  const handleMove = useCallback(
    (e) => {
      if (!visible) return;
      updatePos(e.clientX, e.clientY);
    },
    [visible, updatePos],
  );

  const handleLeave = useCallback(() => setVisible(false), []);

  useEffect(() => {
    if (!visible) return undefined;
    const onScroll = () => setVisible(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [visible]);

  return (
    <>
      <span
        className={`cursor-tooltip-trigger ${className}`.trim()}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onMouseMove={handleMove}
        onFocus={handleEnter}
        onBlur={handleLeave}>
        {children}
      </span>
      {visible && hasContent
        ? createPortal(
            <div
              className="cursor-tooltip-float"
              style={{ left: pos.left, top: pos.top, maxWidth: pos.maxWidth }}
              role="tooltip">
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export default CursorTooltip;
